import { NewPricingBanner } from '@/components/BannerTemplate/NewPricingBanner';
import Layout from '@/components/layout';
import SkeletonPricing from '@/components/SkeletonLayout/SkeletonPricing';
import { config } from '@/config';
import { PLAN_ENUM, PlanSubscription, PricingPlan } from '@/constants/enum';
import useTitleBar from '@/hooks/useTitleBar';
import {
  useGetCurrentDiscountCodeQuery,
  useGetGeneralSettingQuery,
  useGetPricingQuery,
  useUpdateGeneralSettingMutation,
} from '@/redux/query';
import { BlockStack, Box, Button, ButtonGroup, InlineGrid, InlineStack } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlanCard from './component/PlanCard';
import { DiscountModal } from './component/PlanCard/component/DiscountModal';
import { StyledButtonGroup } from './styled';

const Pricing = () => {
  const { t } = useTranslation('pricing');
  const [activePlanIndex, setActivePlanIndex] = useState(PLAN_ENUM.MONTHLY);
  const [isOpenDiscountModal, setIsOpenDiscountModal] = useState(false);
  const { data: generalSettingData, isLoading: isLoadingGeneralSettingData } = useGetGeneralSettingQuery({ shop: config.shop });
  const { data: pricingPlanData } = useGetPricingQuery();
  const { data: currentDiscountCodeData } = useGetCurrentDiscountCodeQuery({ shop: config.shop });

  const essentialPlan = useMemo(
    () => pricingPlanData?.data.find((item) => item.plan === PricingPlan.Essential),
    [pricingPlanData?.data],
  );
  const smartPlan = useMemo(() => pricingPlanData?.data.find((item) => item.plan === PricingPlan.Smart), [pricingPlanData?.data]);
  const enterprisePlan = useMemo(
    () => pricingPlanData?.data.find((item) => item.plan === PricingPlan.Enterprise),
    [pricingPlanData?.data],
  );
  const [updateGeneralSetting] = useUpdateGeneralSettingMutation();

  const handleButtonClick = useCallback(
    (index: number) => {
      if (activePlanIndex === index) return;
      setActivePlanIndex(index);
    },
    [activePlanIndex],
  );

  const subscription = useMemo(
    () => ({
      plan: generalSettingData?.data.plan,
      index: generalSettingData?.data.subscription === PlanSubscription.Monthly ? PLAN_ENUM.MONTHLY : PLAN_ENUM.YEARLY,
    }),
    [generalSettingData?.data.plan, generalSettingData?.data.subscription],
  );

  const PlanCardData = useMemo(() => {
    const plans = [
      {
        plan: PricingPlan.Free,
        subscription: activePlanIndex === PLAN_ENUM.MONTHLY ? PlanSubscription.Monthly : PlanSubscription.Yearly,
        description: 'Perfect for beginners to simplify local delivery and pickup—at no cost.',
        price: 0,
        tag: '',
        serviceInclue: '',
        trialDays: 0,
        listServices: [
          {
            isAvailable: true,
            isInProgress: false,
            detail: '20 orders/ month',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: '1 location',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Flexbile date & time slots',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Cut-off and prep time',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Blackout dates',
          },
          {
            isAvailable: false,
            isInProgress: false,
            detail: 'No specific-product rules',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Order limit per day',
          },
        ],
      },
      {
        plan: PricingPlan.Essential,
        subscription: activePlanIndex === PLAN_ENUM.MONTHLY ? PlanSubscription.Monthly : PlanSubscription.Yearly,
        description: 'For all businesses with same-day delivery and preschedule delivery dates.',
        price: activePlanIndex === PLAN_ENUM.MONTHLY ? Number(essentialPlan?.price) : Number(essentialPlan?.annuallyPrice),
        oldPriceMonth: 6.99,
        tag: essentialPlan?.tag || '',
        serviceInclue: 'All the benefits in Free, and:',
        trialDays: essentialPlan?.trialDays,
        listServices: [
          {
            isAvailable: true,
            isInProgress: false,
            detail: '100 orders/ month ',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: '2 locations',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Specific products rules',
          },
          {
            isAvailable: false,
            isInProgress: false,
            detail: 'No GG calendar integration',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Order limit per time slot',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Minimum order value',
          },
        ],
      },
      {
        plan: PricingPlan.Smart,
        subscription: activePlanIndex === PLAN_ENUM.MONTHLY ? PlanSubscription.Monthly : PlanSubscription.Yearly,
        description: 'Scale your orders and manage multiple locations with ease.',
        price: activePlanIndex === PLAN_ENUM.MONTHLY ? Number(smartPlan?.price) : Number(smartPlan?.annuallyPrice),
        oldPriceMonth: 14.99,
        tag: smartPlan?.tag || '',
        serviceInclue: 'All the greatness in Basic, and:',
        trialDays: smartPlan?.trialDays,
        listServices: [
          {
            isAvailable: true,
            isInProgress: false,
            detail: '2500 orders/ month',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Up to 5 locations',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Product override',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Customized widget design',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'GG calendar',
          },
          {
            isAvailable: true,
            isInProgress: true,
            detail: 'POS integration',
          },
        ],
      },
      {
        plan: PricingPlan.Enterprise,
        subscription: activePlanIndex === PLAN_ENUM.MONTHLY ? PlanSubscription.Monthly : PlanSubscription.Yearly,
        description: 'Everything unlimited, fully customizable, with priority support.',
        price: activePlanIndex === PLAN_ENUM.MONTHLY ? Number(enterprisePlan?.price) : Number(enterprisePlan?.annuallyPrice),
        oldPriceMonth: 24.99,
        tag: enterprisePlan?.tag || '',
        serviceInclue: 'All the greatness in Smart, and:',
        trialDays: enterprisePlan?.trialDays,
        listServices: [
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Unlimited orders/ month ',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Up to 20 locations',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'Advanced text fields',
          },
          {
            isAvailable: true,
            isInProgress: false,
            detail: 'VIP live chat',
          },
          {
            isAvailable: true,
            isInProgress: true,
            detail: 'Advanced rate',
          },
          {
            isAvailable: true,
            isInProgress: true,
            detail: 'Reschedule',
          },
        ],
      },
    ];

    return plans.map((plan) => {
      if (subscription.plan === PricingPlan.Free) {
        return {
          ...plan,
          isActive: subscription.plan === plan.plan,
        };
      }
      return {
        ...plan,
        isActive: subscription.index === activePlanIndex && subscription.plan === plan.plan,
      };
    });
  }, [activePlanIndex, subscription.index, subscription.plan, enterprisePlan, smartPlan, essentialPlan]);

  useEffect(() => {
    if (
      generalSettingData?.data.plan === PricingPlan.Free ||
      generalSettingData?.data.subscription === PlanSubscription.Monthly
    ) {
      setActivePlanIndex(PLAN_ENUM.MONTHLY);
    } else setActivePlanIndex(PLAN_ENUM.YEARLY);
  }, [generalSettingData?.data]);

  useTitleBar({ title: t('page.title') });

  return (
    <Layout>
      {isLoadingGeneralSettingData ? (
        <SkeletonPricing />
      ) : (
        <BlockStack gap="400">
          <NewPricingBanner
            isOpen={Boolean(generalSettingData?.data.showBannerOfferPricing)}
            onClose={() =>
              updateGeneralSetting({
                shop: config.shop,
                showBannerOfferPricing: false,
              })
            }
          />
          <InlineStack align="center">
            <Box width="270px">
              <StyledButtonGroup>
                <ButtonGroup variant="segmented" fullWidth>
                  <Button
                    variant={activePlanIndex === PLAN_ENUM.MONTHLY ? 'primary' : undefined}
                    onClick={() => handleButtonClick(PLAN_ENUM.MONTHLY)}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={activePlanIndex === PLAN_ENUM.YEARLY ? 'primary' : undefined}
                    onClick={() => handleButtonClick(PLAN_ENUM.YEARLY)}
                  >
                    Yearly{' '}
                    {
                      (
                        <span style={{ color: activePlanIndex === PLAN_ENUM.MONTHLY ? 'green' : '#00ff00' }}>(20% off)</span>
                      ) as unknown as string
                    }
                  </Button>
                </ButtonGroup>
              </StyledButtonGroup>
            </Box>
          </InlineStack>
          <InlineStack align="center">
            <Button variant="plain" onClick={() => setIsOpenDiscountModal(true)}>
              {t('discount.title')}
            </Button>
          </InlineStack>
          <InlineGrid columns={{ xs: 1, md: 2, lg: 4 }} gap={'400'}>
            {PlanCardData.map((item, index) => (
              <PlanCard key={index} cardItem={item} currentPlan={subscription.plan} />
            ))}
          </InlineGrid>
        </BlockStack>
      )}
      <DiscountModal
        isOpenDiscountModal={isOpenDiscountModal}
        handleClose={() => setIsOpenDiscountModal(false)}
        currentDiscountCode={currentDiscountCodeData?.data?.code || ''}
      />
    </Layout>
  );
};

export default Pricing;
